import { ref, computed } from 'vue'
import { addDays } from 'date-fns'

const FORECAST_DAYS = 7

function getLocationDates(timezone?: string): Date[] {
  const baseDate = timezone
    ? (() => {
        try {
          const now = new Date()
          const formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          })
          const locationDateStr = formatter.format(now)
          const [year, month, day] = locationDateStr.split('-').map(Number)
          return new Date(Date.UTC(year, month - 1, day))
        } catch (error) {
          console.error(`❌ Error calculating date for timezone ${timezone}:`, error)
          return new Date()
        }
      })()
    : new Date()

  return Array.from({ length: FORECAST_DAYS }, (_, index) => addDays(baseDate, index))
}

export function formatDateForAPI(date: Date): string {
  return date.toISOString().split('T')[0]
}

export function useTimezone(getLanguage: () => string) {
  const currentTimezone = ref<string | undefined>(undefined)

  const loadTimezoneFromStorage = () => {
    const storedTimezone = localStorage.getItem('locationTimezone')
    if (storedTimezone) {
      currentTimezone.value = storedTimezone

    }
  }

  const _storageHandler = (e: StorageEvent) => {
    if (e.key === 'locationTimezone' && e.newValue && e.newValue !== currentTimezone.value) {
      currentTimezone.value = e.newValue
    }
  }

  const watchTimezoneChanges = () => {
    window.addEventListener('storage', _storageHandler)
  }

  const unwatchTimezoneChanges = () => {
    window.removeEventListener('storage', _storageHandler)
  }

  const updateTimezone = (timezone?: string) => {
    if (timezone && timezone !== currentTimezone.value) {
      currentTimezone.value = timezone
      // Persist so consumers reading localStorage directly (useWeatherData
      // when building per-day cache keys, About-forecast metadata) stay in
      // sync. The auto-detect path in App.vue sets settings.location without
      // ever opening Location.vue, so the API response here is the only
      // place that learns the timezone — without persisting, a date switch
      // in table/icon view would then fail with "Timezone not set".
      try { localStorage.setItem('locationTimezone', timezone) } catch {}
    }
  }

  const locationDates = computed(() => {
    try {
      if (!currentTimezone.value) return []
      return getLocationDates(currentTimezone.value)
    } catch (error) {
      console.error('Error in locationDates computed:', error)
      return []
    }
  })

  const getShortDayLabel = (date: Date): string => {
    const language = getLanguage()
    const locale = language === 'zh-hk' ? 'zh-tw' : language
    try {
      return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(date)
    } catch {
      return new Intl.DateTimeFormat('en', { weekday: 'short', timeZone: 'UTC' }).format(date)
    }
  }

  const formattedDayLabels = computed(() =>
    locationDates.value.map(date => getShortDayLabel(date))
  )

  return {
    currentTimezone,
    loadTimezoneFromStorage,
    watchTimezoneChanges,
    unwatchTimezoneChanges,
    updateTimezone,
    locationDates,
    formattedDayLabels
  }
}
